import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalVariant } from '@/components/Modal';
import { TextField } from '@/components/TextField';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ProcedureTemplate } from '@/types/procedure';

interface CreateReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    template: ProcedureTemplate | null;
    onSubmit: (
        name: string,
        description: string | undefined,
        templateId?: string,
    ) => Promise<void>;
}

export function CreateReportModal({
    isOpen,
    onClose,
    template,
    onSubmit,
}: CreateReportModalProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [inProgress, setInProgress] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const handleOpenAutoFocus = (event: Event) => {
        event.preventDefault();
        nameInputRef.current?.focus();
    };

    const handleClose = () => {
        if (inProgress) return;
        setName('');
        setDescription('');
        onClose();
    };

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();
        if (!trimmedName || inProgress) return;
        setInProgress(true);
        try {
            await onSubmit(
                trimmedName,
                trimmedDescription || undefined,
                template?.id,
            );
            setName('');
            setDescription('');
            onClose();
        } finally {
            setInProgress(false);
        }
    };

    const submitLabel = template
        ? t('createModal.next')
        : t('createModal.submit');

    const modalTitle = template
        ? t('createModal.titleFromTemplate', { name: template.name })
        : t('createModal.title');

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={modalTitle}
            variant={ModalVariant.PLAIN}
            onCancel={handleClose}
            onSubmit={handleSubmit}
            submitText={submitLabel}
            submitDisabled={!name.trim()}
            submitInProgress={inProgress}
            size="medium"
            contentTestId="create-report-modal"
            submitButtonTestId="create-report-submit"
            onOpenAutoFocus={handleOpenAutoFocus}
        >
            <div className="flex flex-col gap-6 p-6">
                <TextField
                    ref={nameInputRef}
                    data-testid="create-report-name-input"
                    label={t('createModal.nameLabel')}
                    placeholder={t('createModal.namePlaceholder')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                />
                <TextField
                    data-testid="create-report-description-input"
                    variant="area"
                    label={t('createModal.descriptionLabel')}
                    placeholder={t('createModal.descriptionPlaceholder')}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                />
            </div>
        </Modal>
    );
}
